import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Facebook, Linkedin, Link2, Share2, Check, Instagram, Music2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { shareTargets, copyToClipboard, canNativeShare, nativeShare, webShareOrFallback, facebookShare, warmSharePreview } from "@/lib/share";
import { trackEvent } from "@/lib/track";

const DEFAULT_CHANNELS = ["facebook", "linkedin", "x", "copy", "instagram", "tiktok"];

// Reusable, content-agnostic share dialog (P1.1). Link sharing only. Always shares the
// canonical public URL verbatim.
// Props: open, onOpenChange, url (canonical public URL), title, text, heading, subtitle,
//        entityType, channels (ordered list of channel keys to render).
export function ShareDialog({ open, onOpenChange, url, title = "", text = "", entityId = "", entityType = "listing", channels = DEFAULT_CHANNELS, heading = "Share this listing", subtitle = "Share your public PropertyConnect listing with potential buyers and your professional network." }) {
  const [copied, setCopied] = React.useState(false);
  const targets = shareTargets({ url, text: text || title });
  const ent = { entity_type: entityType, entity_id: entityId || undefined };
  React.useEffect(() => { if (open) { trackEvent("share_modal_opened", ent); warmSharePreview(url); } }, [open]); // eslint-disable-line

  const doCopy = async (hint) => {
    trackEvent("share_copy_link", ent);
    const ok = await copyToClipboard(url);
    if (ok) { setCopied(true); toast.success(hint || "Link copied"); setTimeout(() => setCopied(false), 2000); }
    else toast.error("Could not copy the link");
  };

  const doNative = async () => {
    trackEvent("share_native_selected", ent);
    const res = await nativeShare({ url, title, text: text || title });
    if (res === "unsupported") { await doCopy("Sharing not supported here — link copied instead"); }
  };

  // Robust handoff for LinkedIn/X: new tab → native sheet → copy link (never a no-op).
  const doWebShare = async (href, evtName) => {
    trackEvent(evtName, ent);
    const res = await webShareOrFallback(href, { url, title: text || title });
    if (res === "copied") { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Couldn't open the app here — link copied. Paste it to share."); }
    else if (res === "failed") toast.error("Couldn't open the share option — try Copy Link.");
  };

  // Facebook: native Share Sheet first on touch devices (facebook.com/sharer breaks in
  // iOS/in-app webviews); web sharer on desktop; copy-link as the final fallback.
  const doFacebook = async () => {
    trackEvent("share_facebook_selected", ent);
    const res = await facebookShare({ url, title: text || title });
    if (res === "copied") { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Couldn't open Facebook here — link copied. Paste it into Facebook to share."); }
    else if (res === "failed") toast.error("Couldn't open Facebook — try Copy Link.");
  };

  // Deep-link channels (Messenger/Viber): open the app link; fall back to native share, then
  // copy. The canonical URL is always preserved.
  const doDeepLink = async (href, evtName, appName) => {
    trackEvent(evtName, ent);
    const res = await webShareOrFallback(href, { url, title: text || title });
    if (res === "copied") { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success(`Couldn't open ${appName} here — link copied. Paste it into ${appName} to share.`); }
    else if (res === "failed") toast.error(`Couldn't open ${appName} — try Copy Link.`);
  };

  const Tile = ({ testid, onClick, icon: Icon, label, className = "" }) => (
    <button data-testid={testid} onClick={onClick} className={`flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#0F2C59] hover:bg-slate-50 transition-colors ${className}`}>
      <Icon className="h-6 w-6 text-[#0F2C59]" />
      <span className="text-xs font-semibold text-slate-700">{label}</span>
    </button>
  );

  const TILES = {
    facebook: <Tile key="facebook" testid="share-facebook" onClick={doFacebook} icon={Facebook} label="Facebook" />,
    messenger: <Tile key="messenger" testid="share-messenger" onClick={() => doDeepLink(targets.messenger, "share_messenger_selected", "Messenger")} icon={MessengerIcon} label="Messenger" />,
    whatsapp: <Tile key="whatsapp" testid="share-whatsapp" onClick={() => doDeepLink(targets.whatsapp, "share_whatsapp_selected", "WhatsApp")} icon={WhatsAppIcon} label="WhatsApp" />,
    viber: <Tile key="viber" testid="share-viber" onClick={() => doDeepLink(targets.viber, "share_viber_selected", "Viber")} icon={ViberIcon} label="Viber" />,
    linkedin: <Tile key="linkedin" testid="share-linkedin" onClick={() => doWebShare(targets.linkedin, "share_linkedin_selected")} icon={Linkedin} label="LinkedIn" />,
    x: <Tile key="x" testid="share-x" onClick={() => doWebShare(targets.x, "share_x_selected")} icon={XIcon} label="X" />,
    copy: <Tile key="copy" testid="share-copy" onClick={() => doCopy()} icon={copied ? Check : Link2} label={copied ? "Copied" : "Copy Link"} />,
    instagram: <Tile key="instagram" testid="share-instagram" onClick={() => doCopy("Link copied — paste it into your Instagram post, story or bio")} icon={Instagram} label="Instagram" />,
    tiktok: <Tile key="tiktok" testid="share-tiktok" onClick={() => doCopy("Link copied — add it to your TikTok content where supported")} icon={Music2} label="TikTok" />,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-[#C79A3B]" /> {heading}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mt-1">
          {channels.map((k) => TILES[k]).filter(Boolean)}
        </div>

        <div className="mt-1 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center gap-2">
          <span data-testid="share-url" className="text-xs text-slate-500 truncate flex-1">{url}</span>
          <Button data-testid="share-copy-inline" size="sm" variant="outline" onClick={() => doCopy()} className="shrink-0 rounded-lg h-8">{copied ? "Copied" : "Copy"}</Button>
        </div>

        {canNativeShare() && (
          <Button data-testid="share-native" onClick={doNative} className="w-full rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white font-semibold">
            <Share2 className="h-4 w-4 mr-2" /> More / Share…
          </Button>
        )}
        <p className="text-[11px] text-slate-400 text-center">Instagram &amp; TikTok don’t support direct web links — copy the link, then share it there.</p>
      </DialogContent>
    </Dialog>
  );
}

// Simple X (Twitter) glyph — lucide has no official X mark.
function XIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Brand glyphs (lucide ships no brand marks). Monochrome, inherit currentColor.
function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function ViberIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.294 4.177.693 6.698.623 9.82c-.06 3.11-.13 8.95 5.5 10.541v2.42s-.038.97.602 1.17c.79.25 1.24-.5 1.99-1.3l1.4-1.58c3.85.32 6.81-.42 7.15-.53.78-.25 5.2-.82 5.92-6.68.74-6.04-.36-9.86-2.34-11.58l-.01-.01c-.6-.55-3-2.29-8.36-2.31 0 0-.39-.025-1.07-.02zm.07 1.7c.58-.004.92.018.92.018 4.54.01 6.71 1.38 7.22 1.84 1.67 1.44 2.52 4.88 1.9 9.92-.6 4.9-4.18 5.21-4.84 5.42-.28.09-2.89.74-6.17.53 0 0-2.44 2.94-3.2 3.71-.12.12-.26.17-.35.15-.13-.03-.16-.18-.16-.4l.02-4.02c-4.77-1.32-4.49-6.3-4.44-8.9.06-2.6.55-4.72 2-6.15 1.96-1.77 5.48-2.03 7.11-2.04zm.36 2.6a.3.3 0 00-.3.29.3.3 0 00.29.3c1.85.03 3.35.62 4.55 1.79 1.19 1.16 1.79 2.72 1.83 4.7a.3.3 0 00.3.29.3.3 0 00.29-.3c-.04-2.11-.7-3.85-2-5.13-1.31-1.28-2.98-1.94-4.95-1.98a.3.3 0 00-.01 0zm-5.03.72a.87.87 0 00-.5.13h-.02c-.41.24-.78.55-1.09.92-.26.32-.4.64-.44.95a.85.85 0 00.02.34l.01.02c.14.53.53 1.39 1.29 2.76.49.89 1.06 1.72 1.7 2.51.32.4.66.77 1.05 1.13l.13.12.13.12c.36.39.73.73 1.13 1.05.79.64 1.62 1.21 2.51 1.7 1.37.76 2.23 1.15 2.76 1.29l.02.01a.85.85 0 00.34.02c.31-.04.63-.18.95-.44.37-.31.68-.68.92-1.09v-.02a.87.87 0 00.11-.62c-.07-.31-.28-.58-.55-.79l-1.4-.98c-.31-.21-.62-.3-.9-.24-.28.06-.5.24-.66.5l-.29.36c-.15.19-.42.16-.42.16l-.01.01c-1.98-.51-2.85-2.02-3.02-2.32 0 0-.03-.27.16-.42l.36-.29c.26-.16.44-.38.5-.66.06-.28-.03-.59-.24-.9l-.98-1.4c-.2-.27-.48-.48-.79-.55a.87.87 0 00-.12-.01zm5.06.87a.3.3 0 00-.29.31.3.3 0 00.31.28c1.27.06 2.2.46 2.84 1.14.64.68.97 1.55.94 2.66a.3.3 0 00.29.3.3.3 0 00.3-.29c.03-1.24-.36-2.29-1.11-3.09-.76-.81-1.86-1.28-3.27-1.35a.3.3 0 00-.01 0zm.16 1.8a.3.3 0 00-.02.6c.62.03 1.02.22 1.29.5.27.28.44.7.47 1.35a.3.3 0 00.31.28.3.3 0 00.29-.31c-.03-.74-.24-1.35-.64-1.77-.4-.42-.98-.63-1.68-.65a.3.3 0 00-.02 0z" />
    </svg>
  );
}

function MessengerIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.652V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.13 3.259L19.752 8l-6.561 6.963z" />
    </svg>
  );
}
